/**
 * Application route definitions.
 *
 * All routes are registered here in one place. Feature bolts add routes
 * to this file as they are implemented.
 *
 * Code splitting strategy:
 *   Public auth pages (login, forgot-password, reset-password, accept-invite)
 *   are eagerly imported — they are the first screen unauthenticated users hit.
 *
 *   All protected pages are lazily imported via React.lazy(). Vite splits each
 *   into its own chunk, so users only download the code for pages they visit.
 *   Vendor deps land in stable named chunks (see vite.config.ts manualChunks)
 *   and are cached across deploys independently of app code.
 *
 * Route structure:
 *   /login                 — public
 *   /forgot-password       — public
 *   /reset-password        — public (token in query string)
 *   /accept-invite         — public (BOLT-022)
 *   /                      — protected (super_admin sees trial dashboard; others see dashboard placeholder)
 *   /leads                 — protected (BOLT-074)
 *   /users                 — protected (BOLT-022)
 *   /account-settings      — protected (BOLT-026)
 *   /org-settings          — protected (BOLT-081; API enforces org_owner|org_admin)
 *   /website               — protected (PRD-020; API enforces org_owner|org_admin)
 *   /programs              — protected (BOLT-310; API enforces org_owner|org_admin)
 *   /feedback              — protected (BOLT-297)
 *   /feedback/inbox        — protected + super_admin only (BOLT-297)
 *   /feedback/:id          — protected (BOLT-297; API enforces 403 for non-owners)
 *   /admin/orgs            — protected + super_admin only (BOLT-024)
 *   /admin/orgs/new        — protected + super_admin only (BOLT-051)
 *   /admin/org-import      — protected + super_admin only (BOLT-416)
 *   /admin/trials          — protected + super_admin only (BOLT-024)
 *   /admin/billing         — protected + super_admin only (BOLT-327)
 *   /admin/super-admins    — protected + super_admin only (super-admin invites)
 *   /admin/builds          — protected + super_admin only (builds dashboard)
 *   *                      — 404
 */
import { lazy, Suspense } from 'react';
import { Navigate, Routes, Route, useParams } from 'react-router';
import { AppShell } from './components/layout/AppShell.js';
import { RequireAuth } from './components/auth/RequireAuth.js';
import { RequireRole } from './components/auth/RequireRole.js';

// ── Eager: public auth pages ─────────────────────────────────────────────────
import { LoginPage } from './pages/LoginPage.js';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.js';
import { ResetPasswordPage } from './pages/ResetPasswordPage.js';
import { AcceptInvitePage } from './pages/AcceptInvitePage.js';

// ── Lazy: all protected pages ────────────────────────────────────────────────
// Each becomes its own JS chunk; loaded on first navigation to that route.
const TrialDashboardPage = lazy(() =>
  import('./pages/TrialDashboardPage.js').then((m) => ({ default: m.TrialDashboardPage })),
);
const LeadsPage = lazy(() =>
  import('./pages/LeadsPage.js').then((m) => ({ default: m.LeadsPage })),
);
const LeadCreatePage = lazy(() =>
  import('./pages/LeadCreatePage.js').then((m) => ({ default: m.LeadCreatePage })),
);
const LeadProfilePage = lazy(() =>
  import('./pages/LeadProfilePage.js').then((m) => ({ default: m.LeadProfilePage })),
);
const UsersPage = lazy(() =>
  import('./pages/UsersPage.js').then((m) => ({ default: m.UsersPage })),
);
const AccountSettingsPage = lazy(() =>
  import('./pages/AccountSettingsPage.js').then((m) => ({ default: m.AccountSettingsPage })),
);
const OrgSettingsPage = lazy(() =>
  import('./pages/OrgSettingsPage.js').then((m) => ({ default: m.OrgSettingsPage })),
);
const FeedbackListPage = lazy(() =>
  import('./pages/FeedbackListPage.js').then((m) => ({ default: m.FeedbackListPage })),
);
const FeedbackInboxPage = lazy(() =>
  import('./pages/FeedbackInboxPage.js').then((m) => ({ default: m.FeedbackInboxPage })),
);
const FeedbackThreadPage = lazy(() =>
  import('./pages/FeedbackThreadPage.js').then((m) => ({ default: m.FeedbackThreadPage })),
);
const OrgSearchPage = lazy(() =>
  import('./pages/OrgSearchPage.js').then((m) => ({ default: m.OrgSearchPage })),
);
const OrgDetailPage = lazy(() =>
  import('./pages/OrgDetailPage.js').then((m) => ({ default: m.OrgDetailPage })),
);
const OrgCreateWizardPage = lazy(() =>
  import('./pages/OrgCreateWizardPage.js').then((m) => ({ default: m.OrgCreateWizardPage })),
);
const OrgImportPage = lazy(() =>
  import('./pages/OrgImportPage.js').then((m) => ({ default: m.OrgImportPage })),
);
const TrialsPage = lazy(() =>
  import('./pages/TrialsPage.js').then((m) => ({ default: m.TrialsPage })),
);
const BillingHealthPage = lazy(() =>
  import('./pages/BillingHealthPage.js').then((m) => ({ default: m.BillingHealthPage })),
);
const SuperAdminUsersPage = lazy(() =>
  import('./pages/SuperAdminUsersPage.js').then((m) => ({ default: m.SuperAdminUsersPage })),
);
const AdminBuildsPage = lazy(() =>
  import('./pages/AdminBuildsPage.js').then((m) => ({ default: m.AdminBuildsPage })),
);
const BacklogPage = lazy(() =>
  import('./pages/BacklogPage.js').then((m) => ({ default: m.BacklogPage })),
);
const SystemHealthPage = lazy(() =>
  import('./pages/SystemHealthPage.js').then((m) => ({ default: m.SystemHealthPage })),
);
const EngineeringFixQueuePage = lazy(() =>
  import('./pages/EngineeringFixQueuePage.js').then((m) => ({
    default: m.EngineeringFixQueuePage,
  })),
);
const FixQueueIssuePage = lazy(() =>
  import('./pages/FixQueueIssuePage.js').then((m) => ({ default: m.FixQueueIssuePage })),
);
const WebsitePage = lazy(() =>
  import('./pages/WebsitePage.js').then((m) => ({ default: m.WebsitePage })),
);
const AgentActivityPage = lazy(() =>
  import('./pages/AgentActivityPage.js').then((m) => ({ default: m.AgentActivityPage })),
);
const ProgramsPage = lazy(() =>
  import('./pages/ProgramsPage.js').then((m) => ({ default: m.ProgramsPage })),
);
/**
 * BOLT-425: the post editor lives inside the Website page's editor pane, beside
 * the preview. The old URL keeps landing — on that post, in that pane.
 */
function RedirectPostEditor() {
  const { id = '' } = useParams();
  return <Navigate to={`/website?section=blog&post=${encodeURIComponent(id)}`} replace />;
}

/**
 * Placeholder page for routes not yet implemented by feature bolts.
 */
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div
      style={{ display: 'flex', minHeight: '60vh', alignItems: 'center', justifyContent: 'center' }}
    >
      <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827' }}>{title}</h1>
    </div>
  );
}

export function AppRoutes() {
  return (
    // Suspense boundary catches lazy page chunks loading. Fallback is null
    // (blank content area) since chunks are small and transitions near-instant
    // after the vendor chunks are cached.
    <Suspense fallback={null}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />

        {/* Protected routes — wrapped in RequireAuth + AppShell */}
        <Route
          path="/"
          element={
            <RequireAuth>
              <AppShell>
                <TrialDashboardPage />
              </AppShell>
            </RequireAuth>
          }
        />
        {/* BOLT-074: lead feed — protected, no role restriction (super_admin redirects internally) */}
        <Route
          path="/leads"
          element={
            <RequireAuth>
              <AppShell>
                <LeadsPage />
              </AppShell>
            </RequireAuth>
          }
        />
        {/* Manual lead creation — protected, same access as lead feed */}
        <Route
          path="/leads/new"
          element={
            <RequireAuth>
              <AppShell>
                <LeadCreatePage />
              </AppShell>
            </RequireAuth>
          }
        />
        {/* BOLT-324: Lead profile page — protected, same access as lead feed */}
        <Route
          path="/leads/:id"
          element={
            <RequireAuth>
              <AppShell>
                <LeadProfilePage />
              </AppShell>
            </RequireAuth>
          }
        />
        <Route
          path="/users"
          element={
            <RequireAuth>
              <AppShell>
                <UsersPage />
              </AppShell>
            </RequireAuth>
          }
        />
        {/* BOLT-026: self-service account management for every authenticated user */}
        <Route
          path="/account-settings"
          element={
            <RequireAuth>
              <AppShell>
                <AccountSettingsPage />
              </AppShell>
            </RequireAuth>
          }
        />
        {/* BOLT-081: org-level settings — API enforces requireRole(org_owner|org_admin) */}
        <Route
          path="/org-settings"
          element={
            <RequireAuth>
              <AppShell>
                <OrgSettingsPage />
              </AppShell>
            </RequireAuth>
          }
        />
        {/* BOLT-297: feedback / ticketing — all authenticated users */}
        <Route
          path="/feedback"
          element={
            <RequireAuth>
              <AppShell>
                <FeedbackListPage />
              </AppShell>
            </RequireAuth>
          }
        />
        {/* BOLT-297: feedback inbox — super_admin only */}
        <Route
          path="/feedback/inbox"
          element={
            <RequireAuth>
              <RequireRole role="super_admin">
                <AppShell>
                  <FeedbackInboxPage />
                </AppShell>
              </RequireRole>
            </RequireAuth>
          }
        />
        {/* BOLT-297: ticket thread — all authenticated users; API enforces 403 for non-owners */}
        <Route
          path="/feedback/:id"
          element={
            <RequireAuth>
              <AppShell>
                <FeedbackThreadPage />
              </AppShell>
            </RequireAuth>
          }
        />
        {/* PRD-020: website management — API enforces org_owner|org_admin */}
        <Route
          path="/website"
          element={
            <RequireAuth>
              <AppShell>
                <WebsitePage />
              </AppShell>
            </RequireAuth>
          }
        />
        {/* BOLT-396: the agent record — its own page because it grows by one
            run per audit. Decisions are taken on the field they target
            (Website → SEO); this page is the accounting. */}
        <Route
          path="/website/agent-activity"
          element={
            <RequireAuth>
              <AppShell>
                <AgentActivityPage />
              </AppShell>
            </RequireAuth>
          }
        />
        {/* BOLT-420: the custom page / blog post editor — its own full-width
            route, because the Website page's column is too narrow to write in. */}
        <Route path="/website/pages/:id" element={<RedirectPostEditor />} />
        {/* BOLT-310: programs CRUD — API enforces org_owner|org_admin */}
        <Route
          path="/programs"
          element={
            <RequireAuth>
              <AppShell>
                <ProgramsPage />
              </AppShell>
            </RequireAuth>
          }
        />
        {/* Super-admin routes — RequireAuth → RequireRole(super_admin) → AppShell */}
        <Route
          path="/admin/orgs"
          element={
            <RequireAuth>
              <RequireRole role="super_admin">
                <AppShell>
                  <OrgSearchPage />
                </AppShell>
              </RequireRole>
            </RequireAuth>
          }
        />
        {/* BOLT-051: account creation wizard */}
        <Route
          path="/admin/orgs/new"
          element={
            <RequireAuth>
              <RequireRole role="super_admin">
                <AppShell>
                  <OrgCreateWizardPage />
                </AppShell>
              </RequireRole>
            </RequireAuth>
          }
        />
        {/* BOLT-416: bulk org creation from CSV */}
        <Route
          path="/admin/org-import"
          element={
            <RequireAuth>
              <RequireRole role="super_admin">
                <AppShell>
                  <OrgImportPage />
                </AppShell>
              </RequireRole>
            </RequireAuth>
          }
        />
        {/* BOLT-049: org detail page */}
        <Route
          path="/admin/orgs/:orgId"
          element={
            <RequireAuth>
              <RequireRole role="super_admin">
                <AppShell>
                  <OrgDetailPage />
                </AppShell>
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/trials"
          element={
            <RequireAuth>
              <RequireRole role="super_admin">
                <AppShell>
                  <TrialsPage />
                </AppShell>
              </RequireRole>
            </RequireAuth>
          }
        />
        {/* BOLT-327: Billing Health Dashboard — super_admin only */}
        <Route
          path="/admin/billing"
          element={
            <RequireAuth>
              <RequireRole role="super_admin">
                <AppShell>
                  <BillingHealthPage />
                </AppShell>
              </RequireRole>
            </RequireAuth>
          }
        />
        {/* BOLT-045: System Health Dashboard — super_admin only */}
        <Route
          path="/admin/health"
          element={
            <RequireAuth>
              <RequireRole role="super_admin">
                <AppShell>
                  <SystemHealthPage />
                </AppShell>
              </RequireRole>
            </RequireAuth>
          }
        />
        {/* BOLT-402: Engineering Fix Queue — super_admin only */}
        <Route
          path="/admin/fix-queue"
          element={
            <RequireAuth>
              <RequireRole role="super_admin">
                <AppShell>
                  <EngineeringFixQueuePage />
                </AppShell>
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/fix-queue/:code"
          element={
            <RequireAuth>
              <RequireRole role="super_admin">
                <AppShell>
                  <FixQueueIssuePage />
                </AppShell>
              </RequireRole>
            </RequireAuth>
          }
        />
        {/* super-admin invites: super-admin user management */}
        <Route
          path="/admin/super-admins"
          element={
            <RequireAuth>
              <RequireRole role="super_admin">
                <AppShell>
                  <SuperAdminUsersPage />
                </AppShell>
              </RequireRole>
            </RequireAuth>
          }
        />
        {/* Admin builds dashboard — super_admin only */}
        <Route
          path="/admin/builds"
          element={
            <RequireAuth>
              <RequireRole role="super_admin">
                <AppShell>
                  <AdminBuildsPage />
                </AppShell>
              </RequireRole>
            </RequireAuth>
          }
        />
        {/* R&D Backlog (BOLT-406) — super_admin only */}
        <Route
          path="/admin/backlog"
          element={
            <RequireAuth>
              <RequireRole role="super_admin">
                <AppShell>
                  <BacklogPage />
                </AppShell>
              </RequireRole>
            </RequireAuth>
          }
        />
        {/* Catch-all — 404 */}
        <Route path="*" element={<PlaceholderPage title="404 — Page Not Found" />} />
      </Routes>
    </Suspense>
  );
}
